import React from "react";
import { Box, Typography, FormControl, Select, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EligibilityBanner from "./EligibilityBanner";
import BasePicksGrid from "./BasePicksGrid";
import LeaderboardTable from "./LeaderboardTable";
import TabLoader from "../TabLoader";
import SectionLoader from "../SectionLoader";

const JackpotStatus = ({
  summary,
  leaderboard,
  summaryLoading = false,
  leaderboardLoading = false,
  error,
  onRetry,
  showMacSelector = false,
  macOptions = [],
  selectedMac,
  onMacChange,
}) => {
  const theme = useTheme();
  const t     = theme.custom;
  const initialLoading = !summary && (summaryLoading || leaderboardLoading);

  if (initialLoading) {
    return <TabLoader message="Loading jackpot status..." />;
  }

  if (error && !summary) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text2, mb: 2 }}>{error}</Typography>
        {onRetry && (
          <Typography
            component="button"
            onClick={onRetry}
            sx={{
              fontSize:   "13px",
              color:      t.accent,
              background: "none",
              border:     "none",
              cursor:     "pointer",
              fontWeight: 600,
            }}
          >
            Try again
          </Typography>
        )}
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text3 }}>No jackpot data available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1280px", mx: "auto" }}>
      <Box
        sx={{
          display:        "flex",
          flexDirection:  { xs: "column", md: "row" },
          alignItems:     { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap:            2,
          mb:             3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:   { xs: "22px", md: "28px" },
              fontWeight: 700,
              color:      t.text,
              mb:         0.5,
            }}
          >
            Jackpot Status
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.text3, maxWidth: "520px" }}>
            Every Base-round pick must stay green to qualify for the ₹10 Lakh Jackpot
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {showMacSelector && macOptions.length > 0 && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 240 },
                "& .MuiInputBase-root": { height: "auto" },
              }}
            >
              <Select
                value={selectedMac ?? ""}
                onChange={(e) => onMacChange?.(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "16px",
                  fontSize:     "11px",
                  fontWeight:   600,
                  background:   t.surface2,
                  height:       "auto",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: t.border2,
                  },
                  "& .MuiSelect-select": {
                    py:        0.5,
                    px:        1.5,
                    minHeight: "unset !important",
                    lineHeight: 1.4,
                  },
                }}
              >
                {macOptions.map((mac) => (
                  <MenuItem key={mac.code} value={mac.name}>
                    {mac.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box
            sx={{
              px:           1.5,
              py:           0.5,
              borderRadius: "16px",
              background:   "rgba(40,179,180,0.12)",
              border:       `1px solid rgba(40,179,180,0.25)`,
              fontSize:     "11px",
              fontWeight:   600,
              color:        t.accent,
              whiteSpace:   "nowrap",
            }}
          >
            FY 2026-27
          </Box>
        </Box>
      </Box>

      <Box sx={{ position: "relative", mb: 3 }}>
        {summaryLoading && (
          <Box
            sx={{
              position:       "absolute",
              inset:          0,
              zIndex:         2,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              background:     "rgba(0,0,0,0.25)",
              borderRadius:   "10px",
            }}
          >
            <SectionLoader minHeight={80} />
          </Box>
        )}
        <EligibilityBanner summary={summary.summary} />

        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text, mb: 2 }}>
            Your Base Picks vs Base value
          </Typography>
          <BasePicksGrid picks={summary.basePicks} />
        </Box>
      </Box>

      <Box
        sx={{
          background:   t.surface,
          border:       `1px solid ${t.border2}`,
          borderRadius: "10px",
          overflow:     "hidden",
          position:     "relative",
          minHeight:    leaderboardLoading && !leaderboard.length ? 200 : undefined,
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: `1px solid ${t.border}` }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text }}>
            Jackpot Leaderboard
          </Typography>
          <Typography sx={{ fontSize: "12px", color: t.text3, mt: 0.5 }}>
            All 11 Units — ranked by % green picks, ties broken by ROI
          </Typography>
        </Box>

        {leaderboardLoading ? (
          <SectionLoader minHeight={200} />
        ) : (
          <LeaderboardTable rows={leaderboard} />
        )}
      </Box>

      <Typography
        sx={{
          textAlign: "center",
          fontSize:  "11px",
          color:     t.text3,
          mt:        2,
          mb:        1,
        }}
      >
        *The profit on Return on Investment (ROI) is Not Net. Leader board updates at each quarterly close.
      </Typography>
    </Box>
  );
};

export default JackpotStatus;
